import { useLayoutEffect, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Typography, useTheme } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import type { WorkspaceSpotlightTourDetailTone } from './WorkspaceSpotlightTour';

export const getDetailToneStyles = (
  tone: WorkspaceSpotlightTourDetailTone,
  theme: Theme
) => {
  switch (tone) {
    case 'accent':
      return {
        borderColor: alpha(theme.palette.primary.main, 0.2),
        backgroundColor: alpha(theme.palette.primary.main, 0.06),
        labelColor: alpha(theme.palette.primary.light, 0.88),
      };
    case 'caution':
      return {
        borderColor: alpha(theme.palette.warning.main, 0.22),
        backgroundColor: alpha(theme.palette.warning.main, 0.08),
        labelColor: alpha(theme.palette.warning.dark, 0.88),
      };
    case 'default':
    default:
      return {
        borderColor: alpha(theme.palette.divider, 0.14),
        backgroundColor: alpha(theme.palette.background.default, 0.08),
        labelColor: alpha(theme.palette.text.secondary, 0.82),
      };
  }
};

export function useTourScrollState(resetKey: string) {
  const [scrollBody, setScrollBody] = useState<HTMLDivElement | null>(null);
  const [hasMoreContent, setHasMoreContent] = useState(false);

  useLayoutEffect(() => {
    if (!scrollBody) {
      return undefined;
    }

    scrollBody.scrollTop = 0;
    const update = () => {
      setHasMoreContent(
        scrollBody.scrollTop + scrollBody.clientHeight < scrollBody.scrollHeight - 1
      );
    };
    const frameId = window.requestAnimationFrame(update);
    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(update);
    resizeObserver?.observe(scrollBody);
    Array.from(scrollBody.children).forEach((child) => resizeObserver?.observe(child));
    scrollBody.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    return () => {
      window.cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      scrollBody.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [resetKey, scrollBody]);

  return { scrollBodyRef: setScrollBody, hasMoreContent };
}

export function useMeasuredElementHeight(element: HTMLElement | null, resetKey: string) {
  const [height, setHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (!element) {
      setHeight(null);
      return undefined;
    }

    const update = () => {
      const nextHeight = element.getBoundingClientRect().height;
      setHeight((currentHeight) => currentHeight === nextHeight ? currentHeight : nextHeight);
    };
    update();
    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(update);
    resizeObserver?.observe(element);
    window.addEventListener('resize', update);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [element, resetKey]);

  return height;
}

export function TourScrollHint({ visible }: { readonly visible: boolean }) {
  const theme = useTheme();
  if (!visible) {
    return null;
  }

  return (
    <Box
      data-testid="workspace-spotlight-scroll-hint"
      role="note"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.25,
        py: 0.25,
        color: alpha(theme.palette.text.secondary, 0.9),
        backgroundColor: alpha(theme.palette.background.paper, 0.92),
        flexShrink: 0,
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700 }}>
        Scroll for more
      </Typography>
      <KeyboardArrowDownIcon sx={{ fontSize: '0.9rem' }} />
    </Box>
  );
}
